import axios from "axios";

/**
 * Resolve the backend base URL at runtime.
 *
 * We deploy the same React bundle to multiple hosts (the Emergent preview
 * URL and the production custom domain https://globalseedzim.org). Baking a
 * single ``REACT_APP_BACKEND_URL`` at build time causes forms to escape the
 * production domain and hit the preview backend cross-origin — which then
 * fails for anyone without a preview cookie, presenting as "Network Error".
 *
 * Runtime rule (bulletproof, no build-time coupling):
 *   1. Local dev (``localhost``/``127.0.0.1``) → trust ``REACT_APP_BACKEND_URL``.
 *   2. Bundle env URL points at the current hostname (preview matches preview)
 *      → use ``REACT_APP_BACKEND_URL``.
 *   3. Any other host (custom production domain) → call same-origin (``/api``
 *      via ``window.location.origin``). Emergent's ingress routes ``/api/*``
 *      to the backend on the same host, so this works everywhere.
 */
function _computeBackendUrl() {
    const envUrl = (process.env.REACT_APP_BACKEND_URL || "").trim();
    if (typeof window === "undefined" || !window.location) return envUrl;
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
        return envUrl;
    }
    try {
        if (envUrl) {
            const envHost = new URL(envUrl).hostname;
            if (envHost === host) return envUrl;
        }
    } catch (_e) {
        /* fall through */
    }
    return window.location.origin;
}

export const BACKEND_URL = _computeBackendUrl();
export const API_URL = `${BACKEND_URL}/api`;
export const BRAND_LOGO_URL = `${API_URL}/brand/logo`;
export const BRAND_ICON_URL = `${API_URL}/brand/icon`;

export const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    timeout: 30000,
    headers: { "Content-Type": "application/json" },
});

export function formatApiError(err) {
    // Network / CORS / DNS / timeout — no HTTP response ever arrived.
    if (err && err.response === undefined) {
        return "We could not submit your information at this time. Please check your connection and try again. If the problem continues, contact us on WhatsApp at +263 78 265 8473.";
    }
    const status = err?.response?.status;
    if (status === 422 || status === 400) {
        return "Please review the highlighted fields and submit again.";
    }
    if (status === 429) {
        return "You have submitted this form several times in a short period. Please wait a few minutes and try again.";
    }
    if (status && status >= 500) {
        return "We could not submit your information at this time due to a temporary server issue. Please try again shortly. If the problem continues, contact us on WhatsApp at +263 78 265 8473.";
    }
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
